import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  chakra,
  Flex,
  Heading,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Spacer,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { CaseCluster } from '@/services/API';
import { useConfirmModal } from '@/ui/ConfirmModal/ConfirmModal';
import ColumnVisibility from '@/ui/TanstackTable/Columns/ColumnVisibility';
import ActiveFilters from '@/ui/TanstackTable/Filter/ActiveFilters/ActiveFilters';
import Filters from '@/ui/TanstackTable/Filter/Filters';
import GlobalFilter from '@/ui/TanstackTable/Filter/GlobalFilter/GlobalFilter';
import { FilterType } from '@/ui/TanstackTable/Filter/NewFilterComponent';
import Pagination from '@/ui/TanstackTable/Pagination';
import TanstackTableView from '@/ui/TanstackTable/TanstackTableView';
import { fuzzyFilter } from '@/utils/helpers/fuzzyFilter';
import CreateCaseClusterForm from '../CreateCaseClusterForm/CreateCaseClusterForm';
import useAdminCaseClusterTableStore from './AdminCaseClusterListTableStore';
import getColumns, { AdminCaseClusterListItemType } from './columns';

type Props = {
  data: AdminCaseClusterListItemType[];
  handleDeleteCaseCluster: (caseClusterID: string) => void;
};

export default function AdminCaseClusterListView({ data, handleDeleteCaseCluster }: Props) {
  const { t } = useTranslation();
  const [updateData, setUpdateData] = useState<CaseCluster | null>(null);
  const { isOpen: isFormOpen, onOpen: openForm, onClose: closeForm } = useDisclosure();
  const confirmModalUtils = useConfirmModal();

  const customCloseForm = useCallback(() => {
    setUpdateData(null);
    closeForm();
  }, [closeForm]);

  const customOpenForm = useCallback(
    (newData: CaseCluster | null) => {
      setUpdateData(newData);
      openForm();
    },
    [openForm]
  );

  const columns = getColumns({
    t,
    onDeleteClick: handleDeleteCaseCluster,
    onEditClick: (data) => {
      customOpenForm(data);
    },
    confirmModalUtils,
  });
  const [
    columnFilters,
    setColumnFilters,
    columnVisibility,
    setColumnVisibility,
    columnSizing,
    setColumnSizing,
  ] = useAdminCaseClusterTableStore((state) => [
    state.columnFilters,
    state.setColumnFilters,
    state.columnVisibility,
    state.setColumnVisibility,
    state.columnSizing,
    state.setColumnSizing,
  ]);
  const [globalFilter, setGlobalFilter] = useState<string>('');

  const table = useReactTable({
    data: data,
    initialState: {
      pagination: {
        pageSize: 10,
      },
      sorting: [{ id: 'createdAt', desc: true }],
    },
    state: {
      columnSizing,
      columnVisibility,
      columnFilters,
      globalFilter,
    },
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    onGlobalFilterChange: setGlobalFilter,
    onColumnVisibilityChange: (updater) => {
      const newVisibility = typeof updater === 'function' ? updater(columnVisibility) : updater;
      setColumnVisibility(newVisibility);
    },
    onColumnSizingChange: (updater) => {
      const newSizing = typeof updater === 'function' ? updater(columnSizing) : updater;
      setColumnSizing(newSizing);
    },
    defaultColumn: {},
    columnResizeMode: 'onEnd',
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    columns: columns,
  });
  const filterTypeMappings: Partial<Record<keyof AdminCaseClusterListItemType, FilterType>> = {
    createdAt: FilterType.DATE,
  };
  const possibleFilterOptions = {};

  return (
    <Box maxW="full" p={{ base: 3, md: 3 }}>
      <HStack justifyContent="space-between" my={2}>
        <chakra.h3 fontSize="2xl" fontWeight="bold" textAlign="center">
          {t('admin.caseClusters.caseClusterList.title')}
        </chakra.h3>
        <Modal isOpen={isFormOpen} onClose={customCloseForm}>
          <ModalOverlay />
          <ModalContent>
            <ModalCloseButton />
            <ModalBody>
              <Box p="20px" gap="10px">
                <Heading size="sm" pb="5">
                  {updateData
                    ? t('admin.caseClusters.form.title.update')
                    : t('admin.caseClusters.form.title.create')}
                </Heading>

                <CreateCaseClusterForm data={updateData} closeForm={customCloseForm} />
              </Box>
            </ModalBody>
          </ModalContent>
        </Modal>
        <Button
          background="blue.400"
          color="white"
          size="md"
          data-test="create-meeting-button-popUp"
          onClick={() => {
            customOpenForm(null);
          }}
        >
          {t('admin.caseClusters.button.createNew')}
        </Button>
      </HStack>

      <>
        <Flex gap="20px" justifyContent="center" alignItems="center" mb="4">
          <VStack w="full">
            <HStack w="full">
              <ColumnVisibility table={table} />
              <Filters
                columnFilters={columnFilters}
                setColumnFilters={setColumnFilters}
                table={table}
                filterTypeMapping={filterTypeMappings}
                filterOptionsMapping={possibleFilterOptions}
              />
              <GlobalFilter
                globalFilter={globalFilter}
                setGlobalFilter={setGlobalFilter}
                maxWidth="250px"
              />
              <ActiveFilters
                columnFilters={columnFilters}
                removeItemFunc={(elem) => {
                  setColumnFilters(columnFilters.filter((f) => f.id !== elem.id));
                }}
                filterTypeMapping={filterTypeMappings}
              />
              <Spacer />
              <Pagination table={table} />
            </HStack>
          </VStack>
        </Flex>

        <TanstackTableView table={table} />
      </>
    </Box>
  );
}
